import React, { useCallback, useState } from "react";
import axios from "axios";
import "./DirectSearch.css";
import { stateCities } from "../constants/stateCities";
import { specialities } from "../constants/specialities";

const HcpSearchTest = () => {
  const [searchPerformed, setSearchPerformed] = useState(false);
  const [formData, setFormData] = useState({
    npi: "",
    firstName: "",
    lastName: "",
    address: "",
    state: "",
    city: "",
    mailingCity: "",
    mailingState: "",
    mailingAddress: "",
    licenseState: "",
    licenseNumber: "",
    specialty: "",
    mailing_postal_code: "",
    practice_postal_code: "",
    taxonomy_code: "",
    provider_Credential_Text: "",
    provider_Name_Prefix_Text: "",
  });

  const [isSearchCompleted, setIsSearchCompleted] = useState(false);
  const [results, setResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [exactMatches, setExactMatches] = useState([]);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [popupRowIndex, setPopupRowIndex] = useState(null);
  const [totalPages, setTotalPages] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalExactMatches, setTotalExactMatches] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [directSearchError, setDirectSearchError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [copyMessage, setCopyMessage] = useState("");

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === "npi") {
      const numericValue = value.replace(/\D/g, "").slice(0, 10);
      setFormData((prev) => ({ ...prev, [name]: numericValue }));
      if (numericValue && numericValue.length !== 10) {
        setDirectSearchError("NPI must be exactly 10 digits");
      } else {
        setDirectSearchError("");
      }
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleMailingStateChange = (e) => {
    const mailingState = e.target.value;
    setFormData({ ...formData, mailingState });
    setSearchPerformed(false);
    const mailingCities = stateCities[mailingState] || [];
    setCities(mailingCities);
  };

  const handleStateChange = (e) => {
    const state = e.target.value;
    setFormData({ ...formData, state });
    setCities(stateCities[state] || []);
    setSearchPerformed(false);
  };

  const handleSearch = useCallback(
    async (page = 1) => {
      if (formData.npi && formData.npi.length !== 10) {
        setDirectSearchError("NPI must be exactly 10 digits");
        return;
      }

      const isAllFieldsEmpty = Object.values(formData).every(
        (value) => !value || value.trim() === ""
      );
      if (isAllFieldsEmpty) {
        setDirectSearchError(
          "Please fill at least one field to perform the search."
        );
        return;
      }
      const term = getSearchTerm();
      setSearchTerm(term);
      setDirectSearchError("");
      setSearchPerformed(false);
      setIsSearching(true);
      setIsSearchCompleted(false);
      setExactMatches([]);
      setResults([]);
      setCurrentPage(1);
      setLoading(true);

      const filteredParams = Object.fromEntries(
        Object.entries(formData).filter(([_, value]) => value.trim() !== "")
      );
      const pageNumber = isNaN(Number(page)) ? 1 : Number(page);
      const limitNumber = Number(limit);
      const params = {
        ...filteredParams,
        page: pageNumber,
        limit: limitNumber,
      };
      try {
        const directEndpoint = `${process.env.REACT_APP_BACKEND_URL}/search/direct`;
        const directResponse = await axios.get(directEndpoint, { params });
        if (directResponse?.data?.results?.length > 0) {
          setExactMatches(directResponse.data.results || []);
          setTotalExactMatches(directResponse.data.total || 0);
        }
        setResults([]);
      } catch (err) {
        console.error("Error during search:", err);
        setTotalExactMatches(0);
      } finally {
        setIsSearching(false);
        setIsSearchCompleted(true);
        setSearchPerformed(true);
        setLoading(false);
      }
    },
    [formData, limit]
  );

  const handlePageChange = async (newPage) => {
    if (newPage > 0 && newPage <= Math.ceil(totalExactMatches / limit)) {
      setCurrentPage(newPage);
      setIsSearching(true);
      try {
        const params = {
          ...Object.fromEntries(
            Object.entries(formData).filter(([_, value]) => value.trim() !== "")
          ),
          page: newPage,
          limit: limit,
        };
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/search/direct`,
          { params }
        );
        if (response?.data?.results?.length > 0) {
          setExactMatches(response.data.results || []);
          setTotalExactMatches(response.data.total || 0);
        }
      } catch (err) {
        console.error("Error fetching next page:", err);
      } finally {
        setIsSearching(false);
      }
    }
  };

  const handleCopy = (item) => {
    const copyContent = [
      item.NPI || "",
      item.HCP_first_name || "",
      item.HCP_last_name || "",
      item.Specialty_2 || "",
      item.practice_st || ""
    ].join("|");

    navigator.clipboard
      .writeText(copyContent)
      .then(() => {
        setCopyMessage("Copied to clipboard!");
        setTimeout(() => setCopyMessage(""), 2000);
      })
      .catch((err) => {
        console.error("Failed to copy:", err);
        setCopyMessage("Failed to copy!");
        setTimeout(() => setCopyMessage(""), 2000);
      });
  };

  const handleLicenseStateChange = (e) => {
    const licenseState = e.target.value;
    setFormData({ ...formData, licenseState });
    setSearchPerformed(false);
  };

  const handleView = async (record, index) => {
    if (popupRowIndex === index) {
      setPopupRowIndex(null);
      setSelectedRecord(null);
    } else {
      setPopupRowIndex(index);
      try {
        const { data } = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/search/direct/view/${record.NPI}`
        );
        setSelectedRecord(data.result);
      } catch (err) {
        console.error("Error fetching details:", err);
      }
    }
  };

  const getSearchTerm = () => {
    const {
      firstName,
      lastName,
      npi,
      specialty,
      city,
      state,
      address,
      mailingAddress,
      mailingCity,
      mailingState,
      licenseNumber,
      licenseState,
    } = formData;
    let term = "";

    if (firstName) {
      term += `First Name: ${firstName}`;
    }
    if (lastName) {
      term += term ? `, Last Name: ${lastName}` : `Last Name: ${lastName}`;
    }
    if (npi) {
      term += term ? `, NPI: ${npi}` : `NPI: ${npi}`;
    }
    if (specialty) {
      term += term ? `, Specialty: ${specialty}` : `Specialty: ${specialty}`;
    }
    if (city || state) {
      term += term
        ? `, Location: ${city}, ${state}`
        : `Location: ${city}, ${state}`;
    }
    if (address) {
      term += term ? `, Address: ${address}` : `Address: ${address}`;
    }
    if (mailingAddress) {
      term += term
        ? `, Mailing Address: ${mailingAddress}`
        : `Mailing Address: ${mailingAddress}`;
    }
    if (mailingCity || mailingState) {
      term += term
        ? `, Mailing Location: ${mailingCity}, ${mailingState}`
        : `Mailing Location: ${mailingCity}, ${mailingState}`;
    }
    if (licenseNumber) {
      term += term
        ? `, License Number: ${licenseNumber}`
        : `License Number: ${licenseNumber}`;
    }
    if (licenseState) {
      term += term
        ? `, License State: ${licenseState}`
        : `License State: ${licenseState}`;
    }

    return term || "your search";
  };

  const clearForm = () => {
    setFormData({
      firstName: "",
      lastName: "",
      address: "",
      npi: "",
      specialty: "",
      state: "",
      city: "",
      licenseState: "",
      licenseNumber: "",
      mailingState: "",
      mailingCity: "",
      mailingAddress: "",
    });
    setResults([]);
    setExactMatches([]);
    setSearchPerformed(false);
  };

  return (
    <div className="direct-search-container">
      <header className="header">
        <h1>HCP Search</h1>
      </header>

      {searchPerformed && (
        <p className="search-message">Search has been performed.</p>
      )}

      {loading && <div className="revolving-loader"></div>}

      <div className="search-form">
        <div className="form-row">
          <div className="form-field">
            <label>First Name</label>
            <input
              name="firstName"
              value={formData.firstName}
              onChange={handleInputChange}
              placeholder="Enter First Name"
              disabled={isSearching}
            />
          </div>
          <div className="form-field">
            <label>Last Name</label>
            <input
              name="lastName"
              value={formData.lastName}
              onChange={handleInputChange}
              placeholder="Enter Last Name"
              disabled={isSearching}
            />
          </div>
          <div className="form-field">
            <label>HCP ID</label>
            <input
              name="npi"
              value={formData.npi}
              onChange={handleInputChange}
              placeholder="Enter HCP ID"
              disabled={isSearching}
              inputMode="numeric"
              pattern="\d{10}"
              title="NPI must be 10 digits"
            />
            {directSearchError && formData.npi && (
              <span className="field-error">{directSearchError}</span>
            )}
          </div>
        </div>

        <div className="form-row">
          <div className="form-field">
            <label>State</label>
            <select
              name="state"
              value={formData.state}
              onChange={handleStateChange}
              disabled={isSearching}
            >
              <option value="">Select State</option>
              {Object.keys(stateCities).map((state) => (
                <option key={state} value={state}>
                  {state}
                </option>
              ))}
            </select>
          </div>
          <div className="form-field">
            <label>City</label>
            <select
              name="city"
              value={formData.city}
              onChange={handleInputChange}
              disabled={isSearching}
            >
              <option value="">Select City</option>
              {cities.map((city, index) => (
                <option key={index} value={city}>
                  {city}
                </option>
              ))}
            </select>
          </div>
          <div className="form-field">
            <label>Address</label>
            <input
              name="address"
              value={formData.address}
              onChange={handleInputChange}
              placeholder="Enter Address"
              disabled={isSearching}
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-field">
            <label>Specialty</label>
            <select
              name="specialty"
              value={formData.specialty}
              onChange={handleInputChange}
              disabled={isSearching}
            >
              <option value="">Select Specialty</option>
              {specialities.map((speciality, index) => (
                <option key={index} value={speciality}>
                  {speciality}
                </option>
              ))}
            </select>
          </div>
          <div className="form-field">
            <label>Mailing State</label>
            <select
              name="mailingState"
              value={formData.mailingState}
              onChange={handleMailingStateChange}
              disabled={isSearching}
            >
              <option value="">Select Mailing State</option>
              {Object.keys(stateCities).map((state) => (
                <option key={state} value={state}>
                  {state}
                </option>
              ))}
            </select>
          </div>
          <div className="form-field">
            <label>Mailing City</label>
            <select
              name="mailingCity"
              value={formData.mailingCity}
              onChange={handleInputChange}
              disabled={isSearching}
            >
              <option value="">Select Mailing City</option>
              {cities.map((city, index) => (
                <option key={index} value={city}>
                  {city}
                </option>
              ))}
            </select>
          </div>
          <div className="form-field">
            <label>Mailing Address</label>
            <input
              name="mailingAddress"
              value={formData.mailingAddress}
              onChange={handleInputChange}
              placeholder="Enter Mailing Address"
              disabled={isSearching}
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-field">
            <label>License State</label>
            <select
              name="licenseState"
              value={formData.licenseState}
              onChange={handleLicenseStateChange}
              disabled={isSearching}
            >
              <option value="">Select License State</option>
              {Object.keys(stateCities).map((licenseState) => (
                <option key={licenseState} value={licenseState}>
                  {licenseState}
                </option>
              ))}
            </select>
          </div>
          <div className="form-field">
            <label>License No</label>
            <input
              name="licenseNumber"
              value={formData.licenseNumber}
              onChange={handleInputChange}
              placeholder="Enter License No"
              disabled={isSearching}
            />
          </div>
        </div>

        <div className="actions">
          <button onClick={handleSearch} disabled={isSearching}>
            <img src="/images/search.png" alt="Search" /> Search
          </button>
          <button onClick={clearForm} disabled={isSearching}>
            <img src="/images/clear-format.png" alt="Clear" /> Clear
          </button>
        </div>
      </div>

      {directSearchError && (
        <div className="error-message">
          <p>{directSearchError}</p>
        </div>
      )}

      {exactMatches.length > 0 && (
        <div className="suggestedmatches">
          <h3>Search Results</h3>
          {copyMessage && <div className="copy-message">{copyMessage}</div>}
          <>
            <table className="results-table">
              <thead>
                <tr>
                  <th>HCP/HCO ID</th>
                  <th>First Name</th>
                  <th>Last Name</th>
                  <th>Specialty</th>
                  <th>State</th>
                  <th>City</th>
                  <th>Address</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {exactMatches.map((item, index) => (
                  <React.Fragment key={item.NPI}>
                    <tr>
                      <td>{item.NPI}</td>
                      <td>{item.HCP_first_name}</td>
                      <td>{item.HCP_last_name}</td>
                      <td title={item.Specialty_2}>{item.Specialty_2}</td>
                      <td>{item.practice_st}</td>
                      <td>{item.practice_city}</td>
                      <td>{item.practice_address}</td>
                      <td>
                        <button
                          onClick={() => handleView(item, index)}
                          disabled={isSearching}
                        >
                          View
                        </button>
                        <button
                          style={{ marginLeft: "8px" }}
                          onClick={() => handleCopy(item)}
                          disabled={isSearching}
                        >
                          Copy
                        </button>
                      </td>
                    </tr>
                    {popupRowIndex === index && selectedRecord && (
                      <tr className="details-popup">
                        <td colSpan={8}>
                          <div className="popup-content">
                            <h3>HCP ID: {selectedRecord.NPI}</h3>
                            <p>
                              <strong>Practice Address:</strong>{" "}
                              {selectedRecord.practice_address || "N/A"}
                            </p>
                            <p>
                              <strong>Mailing Address:</strong>{" "}
                              {selectedRecord.mailing_address || "N/A"}
                            </p>
                            <p>
                              <strong>Mailing City:</strong>{" "}
                              {selectedRecord.mailing_city || "N/A"}
                            </p>
                            <p>
                              <strong>Mailing State:</strong>{" "}
                              {selectedRecord.mailing_st || "N/A"}
                            </p>
                            <p>
                              <strong>Mailing Postal Code:</strong>{" "}
                              {selectedRecord.mailing_postal_code || "N/A"}
                            </p>
                            <p>
                              <strong>License Number:</strong>{" "}
                              {selectedRecord.License_Number || "N/A"}
                            </p>
                            <p>
                              <strong>Provider_Credential_Text:</strong>{" "}
                              {selectedRecord.Provider_Credential_Text || "N/A"}
                            </p>
                            <p>
                              <strong>Provider_Name_Prefix_Text:</strong>{" "}
                              {selectedRecord.Provider_Name_Prefix_Text || "N/A"}
                            </p>
                            <p>
                              <strong>Practice Postal Code:</strong>{" "}
                              {selectedRecord.practice_postal_code || "N/A"}
                            </p>
                            <p>
                              <strong>Taxonomy Code:</strong>{" "}
                              {selectedRecord.Taxonomy_Code || "N/A"}
                            </p>
                            <p>
                              <strong>Provider License State:</strong>{" "}
                              {selectedRecord.Provider_License_State || "N/A"}
                            </p>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
            <p>{totalExactMatches} records are found.</p>
            <div className="pagination-controls">
              <button
                type="button"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1 || isSearching}
              >
                Previous
              </button>
              <span>
                Page {currentPage} of {Math.ceil(totalExactMatches / limit)}
              </span>
              <button
                type="button"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={
                  currentPage === Math.ceil(totalExactMatches / limit) ||
                  isSearching
                }
              >
                Next
              </button>
            </div>
          </>
        </div>
      )}
    </div>
  );
};

export default HcpSearchTest;